import { create, type StateCreator } from "zustand";
import { immer } from "zustand/middleware/immer";

import type { NodeId } from "./nodes";

export type PickerMedium = "email" | "sms";
export type PickerOrder = "asc" | "desc";

export interface PickerState {
  medium: PickerMedium;
  search: string;
  orderBy: string;
  order: PickerOrder;
}

export interface TileSelectState {
  open: boolean;
  medium: PickerMedium;
  forFrameId?: string;
  insertAtIndex?: number;
}

interface OpenTileSelectArgs {
  medium: PickerMedium;
  forFrameId?: string;
  insertAtIndex?: number;
}

export interface CloneDialogState {
  open: boolean;
}

interface AnalogSequencingStore {
  isAnalogModeActive: boolean;
  selectedNodeId: NodeId | null;
  connectorEventId: string | null;
  picker: PickerState;
  tileSelect: TileSelectState;
  cloneDialog: CloneDialogState;

  actions: {
    reset: () => void;
    setAnalogModeActive: (active: boolean) => void;
    setConnectorEventId: (eventId: string | null) => void;
    selectNode: (nodeId: NodeId | null) => void;
    resetSequenceView: () => void;
    openTileSelect: (args: OpenTileSelectArgs) => void;
    closeTileSelect: () => void;
    openCloneDialog: () => void;
    closeCloneDialog: () => void;

    picker: {
      setMedium: (medium: PickerMedium) => void;
      setSearch: (search: string) => void;
      setOrder: (orderBy: string, order: PickerOrder) => void;
      reset: () => void;
    };
  };
}

const defaultPicker: PickerState = {
  medium: "email",
  search: "",
  orderBy: "modify_date",
  order: "desc",
};

const defaultTileSelect: TileSelectState = {
  open: false,
  medium: "email",
};

const defaultCloneDialog: CloneDialogState = {
  open: false,
};

export const analogSequencingStore = () => {
  const slice: StateCreator<
    AnalogSequencingStore,
    [["zustand/immer", never]]
  > = (set) => ({
    isAnalogModeActive: false,
    selectedNodeId: null,
    connectorEventId: null,
    picker: { ...defaultPicker },
    tileSelect: { ...defaultTileSelect },
    cloneDialog: { ...defaultCloneDialog },

    actions: {
      reset() {
        set((state) => {
          state.isAnalogModeActive = false;
          state.selectedNodeId = null;
          state.connectorEventId = null;
          state.picker = { ...defaultPicker };
          state.tileSelect = { ...defaultTileSelect };
          state.cloneDialog = { ...defaultCloneDialog };
        });
      },
      setAnalogModeActive(active) {
        set((state) => {
          state.isAnalogModeActive = active;
          if (!active) {
            state.selectedNodeId = null;
            state.connectorEventId = null;
            state.picker = { ...defaultPicker };
            state.tileSelect = { ...defaultTileSelect };
            state.cloneDialog = { ...defaultCloneDialog };
          }
        });
      },
      setConnectorEventId(eventId) {
        set((state) => {
          state.connectorEventId = eventId;
        });
      },
      selectNode(nodeId) {
        set((state) => {
          state.selectedNodeId = nodeId;
        });
      },
      resetSequenceView() {
        set((state) => {
          state.selectedNodeId = null;
          state.tileSelect = { ...defaultTileSelect };
          state.cloneDialog = { ...defaultCloneDialog };
        });
      },
      openTileSelect({ medium, forFrameId, insertAtIndex }) {
        set((state) => {
          state.tileSelect = {
            open: true,
            medium,
            forFrameId,
            insertAtIndex,
          };
          state.picker.medium = medium;
        });
      },
      closeTileSelect() {
        set((state) => {
          state.tileSelect = { ...defaultTileSelect };
        });
      },
      openCloneDialog() {
        set((state) => {
          state.cloneDialog = { open: true };
        });
      },
      closeCloneDialog() {
        set((state) => {
          state.cloneDialog = { ...defaultCloneDialog };
        });
      },

      picker: {
        setMedium(medium) {
          set((state) => {
            state.picker.medium = medium;
          });
        },
        setSearch(search) {
          set((state) => {
            state.picker.search = search;
          });
        },
        setOrder(orderBy, order) {
          set((state) => {
            state.picker.orderBy = orderBy;
            state.picker.order = order;
          });
        },
        reset() {
          set((state) => {
            state.picker = { ...defaultPicker };
          });
        },
      },
    },
  });

  const useStore = create<AnalogSequencingStore>()(immer(slice));
  const init = useStore.getInitialState();

  return {
    testing: { getState: () => useStore.getState() },
    actions: init.actions,
    useIsAnalogModeActive: () => useStore((state) => state.isAnalogModeActive),
    useSelectedNodeId: () => useStore((state) => state.selectedNodeId),
    usePicker: <Type>(selector: (picker: PickerState) => Type) =>
      useStore((state) => selector(state.picker)),
    useTileSelect: () => useStore((state) => state.tileSelect),
    useCloneDialog: () => useStore((state) => state.cloneDialog),
  };
};
